import { observer } from "mobx-react-lite";
import { PropsWithChildren } from "react";

import { DateTime } from "@bps/date-time";
import { Heading, Stack, Text, useTheme } from "@bps/fluent-ui";
import { claimStatusErrors } from "@libs/gateways/acc/AccGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Navigate } from "@ui-components/navigation/Navigate.tsx";
import { ClaimStatusText } from "@ui-components/RefText.tsx";
import { TooltipHost } from "@ui-components/TooltipHost.tsx";

import { ClaimStatusBadgeBaseProps } from "./ClaimStatusBadge.types.ts";

type ClaimStatusBadgeTooltipProps = ClaimStatusBadgeBaseProps &
  PropsWithChildren;

export const ClaimStatusBadgeTooltip: React.FC<ClaimStatusBadgeTooltipProps> =
  observer(({ claimId, status, children }) => {
    const { acc } = useStores();
    const theme = useTheme();

    const claim = claimId && acc.claimsMap.get(claimId);
    if (
      !claim ||
      !claim.errorMessageList.length ||
      !status ||
      !claimStatusErrors.includes(status)
    ) {
      return <>{children}</>;
    }

    const error = claim.errorMessageList[0];

    return (
      <TooltipHost
        calloutBgColor={theme.semanticColors.errorBackground}
        content={
          <Stack
            horizontalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { textAlign: "center" } }}
          >
            <Heading
              styles={{ root: { color: theme.semanticColors.errorText } }}
            >
              <ClaimStatusText code={status} />
            </Heading>
            <div>
              <Heading styles={{ root: { display: "inline" } }}>Date: </Heading>
              <Text>
                {DateTime.fromISO(
                  error.lastRequestedDateTime
                )?.toDayDefaultFormat()}
              </Text>
            </div>
            <div>
              <Heading styles={{ root: { display: "inline" } }}>
                ACC message:{" "}
              </Heading>
              <Text>{error.message}</Text>
            </div>
            <Navigate to={routes.settings.accErrorLog.basePath.path({})}>
              ACC error log
            </Navigate>
          </Stack>
        }
      >
        {children}
      </TooltipHost>
    );
  });
